import { useParams, Link } from '@tanstack/react-router'
import React, { useState, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDropzone } from 'react-dropzone'
import { Button } from './ui/button'
import { Card, CardHeader, CardTitle, CardContent } from './ui/card'
import { Label } from './ui/label'
import { Badge } from './ui/badge'
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import { useCan } from '../contexts/PermissionsContext'
import { PermissionDeniedState } from './PermissionDeniedState'
import { useAuth } from '../hooks/useAuth'
import { setAuthToken } from '../api/client'
import { AppIcon } from './ui/icon'
import {
  mdiUpload, mdiLoading, mdiAlert, mdiInformation,
  mdiCheckCircle, mdiChevronDown, mdiChevronUp, mdiPencil, mdiShape,
  mdiBriefcase, mdiFileSign, mdiAccountDetails, mdiFolder, mdiFunction, mdiAutoFix, mdiSwapHorizontal,
  mdiShieldCheck, mdiGavel, mdiClose, mdiAlertCircle,
} from '@mdi/js'
import { PageHeader } from './shared/PageHeader'
import { SECTION_ICONS } from '../lib/section-icons'

type ConflictStrategy = 'skip' | 'update' | 'error'

interface ImportError {
  code: string
  message: string
  field?: string
  details?: string
}

interface ImportChange {
  type: string      // action: "create", "update", "skip", "error"
  entity: string    // entity type: "property", "asset_type", etc.
  code: string
  description?: string
}

interface ImportResponse {
  success: boolean
  errors: ImportError[]
  warnings: string[]
  results?: { created: Record<string, number>; updated: Record<string, number>; skipped: Record<string, number> }
  preview?: { changes: ImportChange[] }
}

interface FileInfo {
  file: File
  data: any
  version?: string
  types: string[]
  exportDate?: string
  error?: string
}

interface MergeResult {
  data: any
  duplicates: { type: string; code: string; files: string[] }[]
}

const typeIcons: Record<string, string> = {
  properties: mdiPencil,
  property: mdiPencil,
  asset_types: mdiShape,
  asset_type: mdiShape,
  document_types: mdiBriefcase,
  document_type: mdiBriefcase,
  contract_types: mdiFileSign,
  contract_type: mdiFileSign,
  contract_stakeholder_types: mdiAccountDetails,
  contract_stakeholder_type: mdiAccountDetails,
  folder_layouts: mdiFolder,
  folder_layout: mdiFolder,
  derivations: mdiFunction,
  derivation: mdiFunction,
  normalizations: mdiAutoFix,
  normalization: mdiAutoFix,
  property_mapping_rules: mdiSwapHorizontal,
  property_mapping_rule: mdiSwapHorizontal,
  compliance_controls: mdiShieldCheck,
  compliance_control: mdiShieldCheck,
  compliance_rules: mdiGavel,
  compliance_rule: mdiGavel,
}

const typeLabels: Record<string, string> = {
  properties: 'Properties',
  property: 'Properties',
  asset_types: 'Asset Types',
  asset_type: 'Asset Types',
  document_types: 'Document Types',
  document_type: 'Document Types',
  contract_types: 'Contract Types',
  contract_type: 'Contract Types',
  contract_stakeholder_types: 'Contract Stakeholder Types',
  contract_stakeholder_type: 'Contract Stakeholder Types',
  folder_layouts: 'Folder Layouts',
  folder_layout: 'Folder Layouts',
  derivations: 'Derivations',
  derivation: 'Derivations',
  normalizations: 'Normalizations',
  normalization: 'Normalizations',
  property_mapping_rules: 'Property Mapping Rules',
  property_mapping_rule: 'Property Mapping Rules',
  compliance_controls: 'Compliance Controls',
  compliance_control: 'Compliance Controls',
  compliance_rules: 'Compliance Rules',
  compliance_rule: 'Compliance Rules',
}

export function TypesImportPage() {
  const { org } = useParams({ from: '/$org/types/import' })
  const { accessToken } = useAuth()
  const { t } = useTranslation('types')
  // Importing types bulk-writes the whole catalogue. The `types` section is a
  // `configuration.read` route, so a reader can land here by URL — the write
  // half is gated in the page (spec 2026-09-04-02, "Gating").
  const canWriteConfiguration = useCan('configuration.write')

  const [step, setStep] = useState<1 | 2 | 3>(1)
  const [files, setFiles] = useState<FileInfo[]>([])
  const [mergedData, setMergedData] = useState<any>(null)
  const [duplicateWarnings, setDuplicateWarnings] = useState<MergeResult['duplicates']>([])
  const [parseError, setParseError] = useState<string | null>(null)
  const [conflictStrategy, setConflictStrategy] = useState<ConflictStrategy>('skip')
  const [previewResponse, setPreviewResponse] = useState<ImportResponse | null>(null)
  const [isPreviewLoading, setIsPreviewLoading] = useState(false)
  const [applyResponse, setApplyResponse] = useState<ImportResponse | null>(null)
  const [isApplying, setIsApplying] = useState(false)
  const [expandedTypes, setExpandedTypes] = useState<Set<string>>(new Set())
  const [showErrors, setShowErrors] = useState(false)

  useBreadcrumbs([
    { title: 'Home', path: `/${org}` },
    { title: 'Settings', path: `/${org}/settings` },
    { title: 'Types', path: `/${org}/types` },
    { title: 'Import', path: `/${org}/types/import` },
  ])

  const callImportApi = useCallback(async (fileData: any, apply: boolean, onConflict: ConflictStrategy): Promise<ImportResponse> => {
    const token = accessToken || 'pat_demo'
    setAuthToken(token)

    const queryParams = new URLSearchParams()
    if (apply) {
      queryParams.append('apply', 'true')
    }
    queryParams.append('on_conflict', onConflict)

    const response = await fetch(`/api/v1/organizations/${org}/types/import?${queryParams.toString()}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(fileData),
    })

    const data = await response.json()

    if (!response.ok && !data.success && !data.errors) {
      throw new Error(data.detail || data.title || `HTTP error ${response.status}`)
    }

    return data as ImportResponse
  }, [org, accessToken])

  const runDryRun = useCallback(async (fileData: any, strategy: ConflictStrategy) => {
    setIsPreviewLoading(true)
    setPreviewResponse(null)
    try {
      const response = await callImportApi(fileData, false, strategy)
      setPreviewResponse(response)
      setStep(2)
    } catch (error: any) {
      setPreviewResponse({
        success: false,
        errors: [{ code: 'API_ERROR', message: error.message || 'Failed to preview import' }],
        warnings: [],
      })
      setStep(2)
    } finally {
      setIsPreviewLoading(false)
    }
  }, [callImportApi])

  const mergeFiles = useCallback((fileInfos: FileInfo[]): MergeResult => {
    const validFiles = fileInfos.filter(f => !f.error)
    const merged: any = { version: '1.0' }
    const duplicates: MergeResult['duplicates'] = []

    // All type keys that could appear
    const allTypeKeys = Object.keys(typeLabels).filter(k => k.endsWith('s') || k === 'properties')

    for (const typeKey of allTypeKeys) {
      const seenCodes = new Map<string, string>() // code -> filename
      const items: any[] = []

      for (const fi of validFiles) {
        const arr = fi.data[typeKey]
        if (!Array.isArray(arr)) continue

        for (const item of arr) {
          const code = item.code
          if (code && seenCodes.has(code)) {
            duplicates.push({ type: typeKey, code, files: [seenCodes.get(code)!, fi.file.name] })
            // Last file wins: remove previous entry
            const idx = items.findIndex(x => x.code === code)
            if (idx !== -1) items.splice(idx, 1)
          }
          if (code) seenCodes.set(code, fi.file.name)
          items.push(item)
        }
      }

      if (items.length > 0) {
        merged[typeKey] = items
      }
    }

    return { data: merged, duplicates }
  }, [])

  const parseAndAddFiles = useCallback(async (acceptedFiles: File[], existingFiles: FileInfo[]) => {
    const newFileInfos: FileInfo[] = []

    for (const file of acceptedFiles) {
      try {
        const text = await file.text()
        const data = JSON.parse(text)

        if (data.version && data.version !== '1.0') {
          newFileInfos.push({
            file, data: null, error: `Unsupported version: ${data.version}`, types: [],
          })
          continue
        }

        const detectedTypes: string[] = []
        for (const key of Object.keys(typeLabels)) {
          if (data[key] && Array.isArray(data[key]) && data[key].length > 0) {
            detectedTypes.push(key)
          }
        }

        newFileInfos.push({
          file, data,
          version: data.version || undefined,
          types: detectedTypes,
          exportDate: data.metadata?.exported_at || data.metadata?.export_date || undefined,
        })
      } catch {
        newFileInfos.push({
          file, data: null, error: 'Invalid JSON', types: [],
        })
      }
    }

    const allFiles = [...existingFiles, ...newFileInfos]
    setFiles(allFiles)
    setParseError(null)

    const validFiles = allFiles.filter(f => !f.error)
    if (validFiles.length === 0) {
      setParseError('No valid files. Please upload valid types export JSON files.')
      return
    }

    const { data: merged, duplicates } = mergeFiles(allFiles)
    setMergedData(merged)
    setDuplicateWarnings(duplicates)
    runDryRun(merged, conflictStrategy)
  }, [conflictStrategy, mergeFiles, runDryRun])

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return
    setPreviewResponse(null)
    setApplyResponse(null)
    parseAndAddFiles(acceptedFiles, files)
  }, [files, parseAndAddFiles])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/json': ['.json'] },
    multiple: true,
  })

  const handleConflictChange = (strategy: ConflictStrategy) => {
    setConflictStrategy(strategy)
    if (mergedData) {
      runDryRun(mergedData, strategy)
    }
  }

  const handleApply = async () => {
    if (!mergedData) return
    setIsApplying(true)
    try {
      const response = await callImportApi(mergedData, true, conflictStrategy)
      setApplyResponse(response)
      setStep(3)
    } catch (error: any) {
      setApplyResponse({
        success: false,
        errors: [{ code: 'API_ERROR', message: error.message || 'Failed to apply import' }],
        warnings: [],
      })
      setStep(3)
    } finally {
      setIsApplying(false)
    }
  }

  const removeFile = useCallback((index: number) => {
    const newFiles = files.filter((_, i) => i !== index)
    setFiles(newFiles)
    const validFiles = newFiles.filter(f => !f.error)
    if (validFiles.length === 0) {
      setMergedData(null)
      setDuplicateWarnings([])
      setPreviewResponse(null)
      return
    }
    const { data: merged, duplicates } = mergeFiles(newFiles)
    setMergedData(merged)
    setDuplicateWarnings(duplicates)
    runDryRun(merged, conflictStrategy)
  }, [files, mergeFiles, runDryRun, conflictStrategy])

  const handleReset = () => {
    setStep(1)
    setFiles([])
    setMergedData(null)
    setDuplicateWarnings([])
    setParseError(null)
    setPreviewResponse(null)
    setApplyResponse(null)
    setConflictStrategy('skip')
    setExpandedTypes(new Set())
    setShowErrors(false)
  }

  const toggleTypeExpanded = (type: string) => {
    setExpandedTypes(prev => {
      const next = new Set(prev)
      if (next.has(type)) next.delete(type)
      else next.add(type)
      return next
    })
  }

  // Build preview summary table from preview changes
  const previewSummary = previewResponse?.preview?.changes
    ? (() => {
      const summary: Record<string, { create: number; update: number; skip: number; error: number }> = {}
      for (const change of previewResponse.preview!.changes) {
        const entity = change.entity
        if (!summary[entity]) summary[entity] = { create: 0, update: 0, skip: 0, error: 0 }
        const action = change.type.toLowerCase()
        if (action === 'create') summary[entity].create++
        else if (action === 'update') summary[entity].update++
        else if (action === 'skip') summary[entity].skip++
        else if (action === 'error') summary[entity].error++
      }
      return summary
    })()
    : previewResponse?.results
      ? (() => {
        const summary: Record<string, { create: number; update: number; skip: number; error: number }> = {}
        const { created, updated, skipped } = previewResponse.results!
        for (const [type, count] of Object.entries(created || {})) {
          if (!summary[type]) summary[type] = { create: 0, update: 0, skip: 0, error: 0 }
          summary[type].create = count
        }
        for (const [type, count] of Object.entries(updated || {})) {
          if (!summary[type]) summary[type] = { create: 0, update: 0, skip: 0, error: 0 }
          summary[type].update = count
        }
        for (const [type, count] of Object.entries(skipped || {})) {
          if (!summary[type]) summary[type] = { create: 0, update: 0, skip: 0, error: 0 }
          summary[type].skip = count
        }
        return summary
      })()
      : null

  // Build a set of property codes that have the "derived" flag in the uploaded file
  const derivedPropertyCodes = useMemo(() => {
    const codes = new Set<string>()
    if (mergedData?.properties) {
      for (const prop of mergedData.properties) {
        if (prop.flags?.includes('derived')) {
          codes.add(prop.code)
        }
      }
    }
    return codes
  }, [mergedData])

  // Check which derived properties lack derivation rules in the file
  const derivedWithoutRules = useMemo(() => {
    if (!mergedData) return []
    const derivationTargets = new Set<string>()
    if (mergedData.derivations) {
      for (const d of mergedData.derivations) {
        if (d.target_property) derivationTargets.add(d.target_property)
      }
    }
    return Array.from(derivedPropertyCodes).filter(code => !derivationTargets.has(code))
  }, [mergedData, derivedPropertyCodes])

  const hasErrors = (previewResponse?.errors?.length ?? 0) > 0

  if (!canWriteConfiguration) {
    return <PermissionDeniedState permission="configuration.write" />
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader icon={SECTION_ICONS.import} title={t('import.title')} />

      {/* Step Indicator */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <span className={step >= 1 ? 'text-foreground font-medium' : ''}>1. {t('import.step_upload')}</span>
        <span>&rarr;</span>
        <span className={step >= 2 ? 'text-foreground font-medium' : ''}>2. {t('import.step_preview')}</span>
        <span>&rarr;</span>
        <span className={step >= 3 ? 'text-foreground font-medium' : ''}>3. {t('import.step_apply')}</span>
      </div>

      {/* Step 1: File Upload */}
      {step === 1 && (
        <>
          <Card>
            <CardContent className="pt-6">
              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors ${
                  isDragActive ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                }`}
                data-testid="import-dropzone"
              >
                <input {...getInputProps()} />
                <AppIcon tone="muted" path={mdiUpload} size="2xl" className="mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">
                  {isDragActive ? 'Drop the files here' : 'Upload Types Export Files'}
                </h3>
                <p className="text-muted-foreground mb-4">
                  Drag and drop one or more JSON export files here, or click to select
                </p>
                <Button variant="outline" data-testid="select-file-btn">
                  Select Files
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* File List */}
          {files.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Uploaded Files ({files.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {files.map((fi, idx) => (
                    <div
                      key={idx}
                      className={`flex items-center gap-3 p-3 rounded-lg border ${fi.error ? 'border-destructive bg-destructive/5' : 'border-border'}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm truncate">{fi.file.name}</span>
                          <span className="text-xs text-muted-foreground">
                            ({(fi.file.size / 1024).toFixed(1)} KB)
                          </span>
                          {fi.error && (
                            <Badge variant="destructive" className="text-xs flex items-center gap-1">
                              <AppIcon path={mdiAlertCircle} size="xs" />
                              {fi.error}
                            </Badge>
                          )}
                        </div>
                        {!fi.error && fi.types.length > 0 && (
                          <div className="flex gap-1.5 mt-1 flex-wrap">
                            {fi.types.map(t => (
                              <Badge key={t} variant="secondary" className="text-xs flex items-center gap-0.5">
                                {typeIcons[t] && <AppIcon path={typeIcons[t]} size="xs" />}
                                {typeLabels[t] || t}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => removeFile(idx)}
                        className="text-muted-foreground hover:text-destructive transition-colors p-1"
                        data-testid={`remove-file-${idx}`}
                      >
                        <AppIcon path={mdiClose} size="sm" />
                      </button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Duplicate Warnings */}
          {duplicateWarnings.length > 0 && (
            <Card className="border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30">
              <CardContent className="pt-4">
                <div className="flex items-start gap-2">
                  <AppIcon path={mdiAlert} size="md" className="text-amber-600 dark:text-amber-400 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800 dark:text-amber-300 text-sm">Duplicate entries detected</p>
                    <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
                      The following entries appear in multiple files. The last file's version will be used.
                    </p>
                    <ul className="text-sm text-amber-700 dark:text-amber-400 mt-1 list-disc list-inside">
                      {duplicateWarnings.map((d, i) => (
                        <li key={i}>
                          <span className="font-mono text-xs">{d.code}</span> in {typeLabels[d.type] || d.type} (from {d.files.join(' → ')})
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {isPreviewLoading && (
            <Card>
              <CardContent className="pt-6 flex items-center justify-center gap-2">
                <AppIcon path={mdiLoading} size="lg" className="motion-safe:animate-spin" />
                <span>Analyzing files...</span>
              </CardContent>
            </Card>
          )}

          {parseError && (
            <Card className="border-destructive">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 text-destructive">
                  <AppIcon path={mdiAlert} size="md" />
                  <span className="font-medium">Invalid file:</span>
                  <span>{parseError}</span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Info Card */}
          <Card className="bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800">
            <CardContent className="pt-4">
              <div className="flex items-start gap-3">
                <AppIcon path={mdiInformation} size="lg" className="text-blue-600 dark:text-blue-400 mt-0.5" />
                <div className="text-sm text-blue-800 dark:text-blue-300">
                  <p className="font-medium mb-1">About Import</p>
                  <p>
                    Upload one or more JSON files previously exported from Types Management. When multiple files
                    are uploaded, they are merged before import. If entries share the same code, the last file wins.
                    The import wizard will preview all changes before applying them.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Step 2: Preview (Dry Run) */}
      {step === 2 && (
        <>
          {/* File Info */}
          {files.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">
                  {files.filter(f => !f.error).length === 1 ? 'File Information' : `Files (${files.filter(f => !f.error).length})`}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {files.filter(f => !f.error).map((fi, idx) => (
                    <div key={idx} className="flex items-center gap-3 text-sm">
                      <span className="font-medium">{fi.file.name}</span>
                      <span className="text-muted-foreground">({(fi.file.size / 1024).toFixed(1)} KB)</span>
                      <div className="flex gap-1 flex-wrap">
                        {fi.types.map(t => (
                          <Badge key={t} variant="secondary" className="text-xs flex items-center gap-0.5">
                            {typeIcons[t] && <AppIcon path={typeIcons[t]} size="xs" />}
                            {typeLabels[t] || t}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Duplicate warnings in preview */}
          {duplicateWarnings.length > 0 && (
            <Card className="border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30">
              <CardContent className="pt-4">
                <div className="flex items-start gap-2">
                  <AppIcon path={mdiAlert} size="md" className="text-amber-600 dark:text-amber-400 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800 dark:text-amber-300 text-sm">Duplicate entries detected</p>
                    <ul className="text-sm text-amber-700 dark:text-amber-400 mt-1 list-disc list-inside">
                      {duplicateWarnings.map((d, i) => (
                        <li key={i}>
                          <span className="font-mono text-xs">{d.code}</span> in {typeLabels[d.type] || d.type} (from {d.files.join(' → ')})
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Conflict Strategy */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Conflict Resolution</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row gap-3">
                {([
                  { value: 'skip' as ConflictStrategy, label: 'Skip', desc: 'Skip existing types, only create new ones' },
                  { value: 'update' as ConflictStrategy, label: 'Update', desc: 'Update existing types with imported values' },
                  { value: 'error' as ConflictStrategy, label: 'Error', desc: 'Stop on any conflict with existing types' },
                ]).map(option => (
                  <Label
                    key={option.value}
                    className={`flex-1 flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                      conflictStrategy === option.value ? 'border-primary bg-primary/5' : 'hover:bg-accent/50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="conflict_strategy"
                      value={option.value}
                      checked={conflictStrategy === option.value}
                      onChange={() => handleConflictChange(option.value)}
                      className="mt-1"
                      data-testid={`conflict-${option.value}`}
                    />
                    <div>
                      <div className="font-medium">{option.label}</div>
                      <div className="text-xs text-muted-foreground">{option.desc}</div>
                    </div>
                  </Label>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Loading state */}
          {isPreviewLoading && (
            <Card>
              <CardContent className="pt-6 flex items-center justify-center gap-2">
                <AppIcon path={mdiLoading} size="lg" className="motion-safe:animate-spin" />
                <span>Loading preview...</span>
              </CardContent>
            </Card>
          )}

          {/* Preview Summary */}
          {!isPreviewLoading && previewSummary && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Preview Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 pr-4">Type</th>
                        <th className="text-right py-2 px-3">To Create</th>
                        <th className="text-right py-2 px-3">To Update</th>
                        <th className="text-right py-2 px-3">To Skip</th>
                        <th className="text-right py-2 pl-3">Errors</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(previewSummary).map(([type, counts]) => {
                        const isExpanded = expandedTypes.has(type)
                        const changes = isExpanded ? previewResponse?.preview?.changes?.filter(c => c.entity === type) : undefined
                        return (
                          <React.Fragment key={type}>
                            <tr className="border-b last:border-0">
                              <td className="py-2 pr-4">
                                <button
                                  onClick={() => toggleTypeExpanded(type)}
                                  className="flex items-center gap-1.5 hover:text-primary transition-colors"
                                  data-testid={`toggle-${type}`}
                                >
                                  <AppIcon path={isExpanded ? mdiChevronUp : mdiChevronDown} size="xs" />
                                  {typeIcons[type] && <AppIcon tone="muted" path={typeIcons[type]} size="xs" />}
                                  <span className="font-medium">{typeLabels[type] || type}</span>
                                </button>
                              </td>
                              <td className="text-right py-2 px-3">{counts.create > 0 ? <Badge variant="default">{counts.create}</Badge> : '—'}</td>
                              <td className="text-right py-2 px-3">{counts.update > 0 ? <Badge variant="secondary">{counts.update}</Badge> : '—'}</td>
                              <td className="text-right py-2 px-3">{counts.skip > 0 ? <Badge variant="outline">{counts.skip}</Badge> : '—'}</td>
                              <td className="text-right py-2 pl-3">{counts.error > 0 ? <Badge variant="destructive">{counts.error}</Badge> : '—'}</td>
                            </tr>
                            {isExpanded && changes && changes.length > 0 && (
                              <tr>
                                <td colSpan={5} className="pb-3 pt-1">
                                  <div className="p-3 bg-muted/50 rounded-lg">
                                    <h4 className="text-sm font-medium mb-2 flex items-center gap-1.5">
                                      {typeIcons[type] && <AppIcon path={typeIcons[type]} size="xs" />}
                                      {typeLabels[type] || type} — Planned Changes
                                    </h4>
                                    <div className="space-y-1">
                                      {changes.map((change, i) => (
                                        <div key={i} className="flex items-center gap-2 text-sm">
                                          <Badge variant={change.type === 'create' ? 'default' : change.type === 'update' ? 'secondary' : 'outline'} className="text-xs w-16 justify-center">
                                            {change.type}
                                          </Badge>
                                          <span className="font-mono text-xs">{change.code}</span>
                                          {(change.entity === 'property' || change.entity === 'properties') && derivedPropertyCodes.has(change.code) && (
                                            <Badge variant="secondary" className="text-xs">Derived</Badge>
                                          )}
                                          {change.description && <span className="text-muted-foreground text-xs">— {change.description}</span>}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Warnings */}
          {!isPreviewLoading && previewResponse?.warnings && previewResponse.warnings.length > 0 && (
            <Card className="border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30">
              <CardContent className="pt-4">
                <div className="flex items-start gap-2">
                  <AppIcon path={mdiAlert} size="md" className="text-amber-600 dark:text-amber-400 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800 dark:text-amber-300 text-sm">Warnings</p>
                    <ul className="text-sm text-amber-700 dark:text-amber-400 mt-1 list-disc list-inside">
                      {previewResponse.warnings.map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Errors */}
          {!isPreviewLoading && hasErrors && (
            <Card className="border-destructive">
              <CardContent className="pt-4">
                <button
                  onClick={() => setShowErrors(!showErrors)}
                  className="flex items-center gap-2 text-destructive w-full"
                  data-testid="toggle-errors"
                >
                  <AppIcon path={mdiAlert} size="md" />
                  <span className="font-medium">
                    {previewResponse!.errors.length} error{previewResponse!.errors.length > 1 ? 's' : ''} found
                  </span>
                  <AppIcon path={showErrors ? mdiChevronUp : mdiChevronDown} size="xs" className="ml-auto" />
                </button>
                {showErrors && (
                  <div className="mt-3 space-y-2 max-h-60 overflow-y-auto">
                    {previewResponse!.errors.map((err, i) => (
                      <div key={i} className="text-sm p-2 bg-destructive/5 rounded">
                        <span className="font-mono text-xs text-destructive">[{err.code}]</span>{' '}
                        <span>{err.message}</span>
                        {err.field && <span className="text-muted-foreground"> (field: {err.field})</span>}
                        {err.details && <p className="text-xs text-muted-foreground mt-1">{err.details}</p>}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Actions */}
          {!isPreviewLoading && (
            <div className="flex justify-between">
              <Button variant="outline" onClick={handleReset} data-testid="back-to-upload-btn">
                Upload Different Files
              </Button>
              <Button
                onClick={handleApply}
                disabled={isApplying || hasErrors}
                data-testid="apply-import-btn"
              >
                {isApplying ? (
                  <>
                    <AppIcon path={mdiLoading} size="md" className="mr-2 motion-safe:animate-spin" />
                    Applying...
                  </>
                ) : (
                  'Apply Import'
                )}
              </Button>
            </div>
          )}
        </>
      )}

      {/* Step 3: Results */}
      {step === 3 && applyResponse && (
        <>
          {applyResponse.success ? (
            <Card className="border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-950/30">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-4">
                  <AppIcon path={mdiCheckCircle} size="xl" className="text-green-600 dark:text-green-400" />
                  <div>
                    <h2 className="text-lg font-semibold text-green-800 dark:text-green-300">Import Successful</h2>
                    <p className="text-sm text-green-700 dark:text-green-400">All types have been imported successfully.</p>
                  </div>
                </div>

                {applyResponse.results && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-green-200 dark:border-green-800">
                          <th className="text-left py-2 pr-4 text-green-800 dark:text-green-300">Type</th>
                          <th className="text-right py-2 px-3 text-green-800 dark:text-green-300">Created</th>
                          <th className="text-right py-2 px-3 text-green-800 dark:text-green-300">Updated</th>
                          <th className="text-right py-2 pl-3 text-green-800 dark:text-green-300">Skipped</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const allTypes = new Set([
                            ...Object.keys(applyResponse.results!.created || {}),
                            ...Object.keys(applyResponse.results!.updated || {}),
                            ...Object.keys(applyResponse.results!.skipped || {}),
                          ])
                          return Array.from(allTypes).map(type => (
                            <tr key={type} className="border-b border-green-100 dark:border-green-900 last:border-0">
                              <td className="py-2 pr-4 flex items-center gap-1.5">
                                {typeIcons[type] && <AppIcon path={typeIcons[type]} size="xs" className="text-green-700 dark:text-green-400" />}
                                <span className="font-medium text-green-800 dark:text-green-300">{typeLabels[type] || type}</span>
                              </td>
                              <td className="text-right py-2 px-3 text-green-800 dark:text-green-300">{applyResponse.results!.created?.[type] || 0}</td>
                              <td className="text-right py-2 px-3 text-green-800 dark:text-green-300">{applyResponse.results!.updated?.[type] || 0}</td>
                              <td className="text-right py-2 pl-3 text-green-800 dark:text-green-300">{applyResponse.results!.skipped?.[type] || 0}</td>
                            </tr>
                          ))
                        })()}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card className="border-destructive">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-4">
                  <AppIcon path={mdiAlert} size="xl" className="text-destructive" />
                  <div>
                    <h2 className="text-lg font-semibold text-destructive">Import Failed</h2>
                    <p className="text-sm text-muted-foreground">The import could not be completed.</p>
                  </div>
                </div>
                {applyResponse.errors?.length > 0 && (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {applyResponse.errors.map((err, i) => (
                      <div key={i} className="text-sm p-2 bg-destructive/5 rounded">
                        <span className="font-mono text-xs text-destructive">[{err.code}]</span>{' '}
                        <span>{err.message}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Warning for derived properties without rules */}
          {applyResponse.success && derivedWithoutRules.length > 0 && (
            <Card className="border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30">
              <CardContent className="pt-4">
                <div className="flex items-start gap-2">
                  <AppIcon path={mdiAlert} size="md" className="text-amber-600 dark:text-amber-400 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800 dark:text-amber-300 text-sm">Derived properties without derivation rules</p>
                    <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
                      The following properties are marked as derived but no derivation rules target them:
                    </p>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {derivedWithoutRules.map(code => (
                        <Badge key={code} variant="secondary" className="font-mono text-xs">{code}</Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Warnings on success */}
          {applyResponse.success && applyResponse.warnings && applyResponse.warnings.length > 0 && (
            <Card className="border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30">
              <CardContent className="pt-4">
                <div className="flex items-start gap-2">
                  <AppIcon path={mdiAlert} size="md" className="text-amber-600 dark:text-amber-400 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800 dark:text-amber-300 text-sm">Warnings</p>
                    <ul className="text-sm text-amber-700 dark:text-amber-400 mt-1 list-disc list-inside">
                      {applyResponse.warnings.map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Actions */}
          <div className="flex justify-between">
            <Button variant="outline" onClick={handleReset} data-testid="import-again-btn">
              Import Again
            </Button>
            <Button asChild data-testid="back-to-types-btn">
              <Link to="/$org/types" params={{ org }}>
                Back to Types
              </Link>
            </Button>
          </div>
        </>
      )}
    </div>
  )
}
